import type { LargeShiftDetailsProps, ReceiptProps } from '@tempmee/react-blocks';
import { isEmployeeOptionShift } from '~/components/WorkerClassification/utils';
import { CONVENIENCE_FEE_INFO, OVERTIME_INFO } from '~/constants/billing';
import { PROMO_ROW_LABEL } from '~/constants/promo';
import { showCandidateOvertime } from '~/growthbook';
import type { ICounterCover } from '~/models';
import { formatMoney, formatMoneyCredit } from '~/utils/formatMoney';
import { feePercent } from '../../shared/utils/shiftPanelsProps';
import { toShiftSummaryFields } from '../../shared/utils/shiftSummaryProps';
import type { TOpenShiftJob } from '../../OpenShiftDetailsPage/utils/openShiftDetailsMappers';

/**
 * One charge line, as the block takes them. `ReceiptLine` itself is not a public
 * export, so the type is read off the prop it belongs to.
 */
type TReceiptLine = ReceiptProps['lines'][number];

/**
 * Fields a completed shift carries that the shared `IJob` model does not declare.
 *
 * `TOpenShiftJob` already widens `procedures` and `autofillOptions`; `counterCover`
 * is the same case — the legacy receipt reads it straight off the payload, but the
 * only declaration of it sits on the transactions model rather than on `IJob`.
 */
export type TCompletedShiftJob = TOpenShiftJob & {
  readonly counterCover?: ICounterCover | null;
};

/** The `LargeShiftDetails` props this page derives; the rest are block defaults. */
export type TCompletedShiftSummary = Pick<
  LargeShiftDetailsProps,
  'status' | 'date' | 'startTime' | 'endTime' | 'totalHours' | 'position' | 'rate'
>;

/** Placeholder the legacy shows for an employee fee the backend has not settled yet. */
const UNKNOWN_AMOUNT = '$--.--';

/**
 * An hour count for a receipt label, e.g. "8.50".
 *
 * Assigned before it reaches a template literal on purpose: the Betterer
 * "no new hand-formatted money strings" ratchet matches an interpolation that
 * calls `toFixed` right after a currency sign, and cannot tell an hour count from
 * a hand-rolled money string.
 */
const toHoursLabel = (hours: number | null | undefined): string => Number(hours ?? 0).toFixed(2);

/**
 * Maps a completed shift onto the `LargeShiftDetails` summary. Presentational
 * values only — the block formats nothing itself.
 */
export const toCompletedShiftSummary = (job: TCompletedShiftJob): TCompletedShiftSummary => ({
  ...toShiftSummaryFields(job),
  // The gate routes only `JobStatusEnum.Complete` here, so the lifecycle state is
  // fixed rather than derived from a status table. It is the only field the shift
  // alone does not decide, which is why the shared mapper leaves it out.
  status: 'completed',
});


/**
 * Maps a completed shift onto the `Receipt` charge lines — the legacy
 * `renderFilledJobInfo` rows, in the same order and with the same conditions.
 *
 * `showCandidateOvertime` is the growthbook split the legacy already applies: off,
 * the pay is one "Professional Pay" line; on, it splits into regular and overtime
 * and each side is dropped when it is zero.
 *
 * The `Professional pay charge date` row the legacy renders for a far-future
 * multi-day booking is deliberately absent: it is gated on the shift starting more
 * than 28 days out, which a completed shift never is.
 */
export const toCompletedReceiptLines = (job: TCompletedShiftJob): TReceiptLine[] => {
  const lines: TReceiptLine[] = [];

  if (!showCandidateOvertime()) {
    lines.push({
      label: `Professional Pay ${toHoursLabel(job.totalHours)} hours`,
      value: formatMoney(job.amount),
    });
  } else {
    const regularHours = job.regularAndOvertimeHours?.regular ?? 0;
    const overtimeHours = job.regularAndOvertimeHours?.overtime ?? 0;

    if (regularHours > 0 && job.totalRegular > 0) {
      lines.push({
        label: `Regular Rate Pay (${toHoursLabel(regularHours)} hrs)`,
        value: formatMoney(job.totalRegular),
      });
    }

    if (overtimeHours > 0 && job.totalOvertime > 0) {
      lines.push({
        label: `Overtime Pay (${toHoursLabel(overtimeHours)} hrs)`,
        value: formatMoney(job.totalOvertime),
        info: OVERTIME_INFO,
      });
    }
  }

  lines.push({
    // `feePercent` returns '' for a zero percentage, so the label degrades to a
    // bare "Service Fee" exactly as the legacy row does.
    label: `Service Fee${feePercent(null, job.tm_fee_percent)}`,
    value: formatMoney(job.tm_fee),
  });

  if (isEmployeeOptionShift(job)) {
    lines.push({
      label: 'Employee Fee',
      value: job.employeeOptionFee ? formatMoney(job.employeeOptionFee) : UNKNOWN_AMOUNT,
    });
  }

  if (job.convenience_fee_percent !== 0) {
    lines.push({
      label: `Convenience Fee${feePercent(null, job.convenience_fee_percent)}`,
      value: formatMoney(job.convenience_fee),
      info: CONVENIENCE_FEE_INFO,
    });
  }

  if (job.counterCover) {
    lines.push({
      label: 'GoTu Counter Cover',
      value: formatMoneyCredit(job.counterCover.amount),
    });
  }

  if (job.promoDiscount?.amount) {
    lines.push({
      label: PROMO_ROW_LABEL,
      value: formatMoneyCredit(job.promoDiscount.amount),
    });
  }

  return lines;
};

/**
 * The receipt's pre-formatted total.
 *
 * The legacy labels this row "Total Pay:"; the block's total label is hardcoded
 * "Total" with no override, so the wording changes by one word. Raised for the
 * design system rather than worked around with a fake line.
 */
export const toCompletedReceiptTotal = (job: TCompletedShiftJob): string => formatMoney(job.total);

